// Tour em slides full-screen, reutilizável (Home, Explorar, Clube, onboarding).
// Mesma casca do ClubWelcomeTour, mas genérica: recebe `cards` prontos.
// Os "prints" são mini-ilustrações JSX que rendem com a paleta do app (sem screenshots).
import React, { useState } from 'react';
import { Icon } from './icons';

export type TourCard = {
  key: string;
  illo?: React.ReactNode;
  eyebrow: string;
  title: string;
  body?: string;
  isList?: boolean;
  list?: Array<[string, string, string]>; // [iconName, titulo, descricao]
  final?: boolean;
};

// ── Mini-ilustrações genéricas (reaproveitáveis pelos tutoriais) ──────────────
// `nome`/`usuario` vêm do perfil quando a pessoa já tem: o "@seu.usuario"
// genérico parecia que o app não sabia quem ela era (pente fino, QA-09).
export function MiniProfilePreview({ nome, usuario }: { nome?: string | null; usuario?: string | null } = {}) {
  return (
    <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: 14, boxShadow: '0 8px 24px -12px rgba(0,0,0,0.4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, var(--ember), #C08CAE)', flexShrink: 0, border: '2px solid var(--paper)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Instrument Serif, Georgia, serif', fontSize: 18, color: 'var(--ink)' }}>{nome || 'Você'}</div>
          <div style={{ fontSize: 12, color: 'var(--ember)', fontFamily: 'monospace' }}>@{usuario || 'seu.usuario'}</div>
        </div>
        <Icon name="check" size={16} color="var(--ember)" />
      </div>
      <div style={{ marginTop: 11, fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic', lineHeight: 1.4 }}>“Leitor de carteirinha — aqui pra criar o hábito.”</div>
    </div>
  );
}

export function MiniBellPreview() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 16, padding: '13px 15px', boxShadow: '0 8px 24px -10px rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', gap: 12, maxWidth: 320 }}>
        <div style={{ width: 38, height: 38, borderRadius: 11, background: 'linear-gradient(135deg, var(--ember), #C7531A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="bell" size={19} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>Forjamente</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 1 }}>Sua leitura de hoje te espera 🔥</div>
        </div>
      </div>
    </div>
  );
}

export function MiniBookPreview() {
  return (
    <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: 14, boxShadow: '0 8px 24px -12px rgba(0,0,0,0.4)' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ width: 44, height: 64, borderRadius: 5, background: 'linear-gradient(135deg, #1C6E63, #0E3B35)', flexShrink: 0, boxShadow: 'inset -2px 0 0 rgba(0,0,0,0.25)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Instrument Serif, Georgia, serif', fontSize: 16, color: 'var(--ink)', lineHeight: 1.15 }}>Seu primeiro livro</div>
          <div style={{ height: 7, borderRadius: 999, background: 'var(--paper-3)', marginTop: 9, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: '32%', borderRadius: 999, background: 'linear-gradient(90deg, var(--ember), #E87A3B)' }} />
          </div>
          {/* Um número inventado ("+18 páginas registradas hoje") aparecia para
              quem tinha lido 11 (pente fino, QA-09). É ilustração: diz que é. */}
          <div style={{ fontSize: 11, color: 'var(--ember)', marginTop: 6, fontWeight: 600 }}>Exemplo: as páginas do dia somam aqui</div>
        </div>
      </div>
    </div>
  );
}

export function MiniSearchPreview() {
  const rows = [['Marina', '@marina'], ['Rafael', '@rafa.le'], ['Clube da Meia-Noite', 'clube · 12 membros']];
  return (
    <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 14, padding: 12, boxShadow: '0 8px 24px -12px rgba(0,0,0,0.4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, background: 'var(--paper-3)', borderRadius: 10, padding: '9px 11px', marginBottom: 10 }}>
        <Icon name="search" size={15} color="var(--ink-3)" />
        <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>Buscar pessoas e clubes…</span>
      </div>
      {rows.map(([name, sub], i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 2px' }}>
          <div style={{ width: 30, height: 30, borderRadius: i === 2 ? 9 : '50%', background: i === 2 ? 'var(--ember-soft, #3C2A1C)' : 'linear-gradient(135deg, #7FA8A6, #4A6E6C)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, color: 'var(--ink)', fontWeight: 500 }}>{name}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{sub}</div>
          </div>
          {i < 2 && <span style={{ fontSize: 11, color: 'var(--ember)', border: '1px solid var(--ember)', borderRadius: 999, padding: '4px 11px', fontWeight: 600 }}>Seguir</span>}
        </div>
      ))}
    </div>
  );
}

export const SlideTour: React.FC<{
  open: boolean;
  cards: TourCard[];
  onClose: () => void;
  skipLabel?: string;
  startLabel?: string;
}> = ({ open, cards, onClose, skipLabel = 'Pular', startLabel = 'Começar' }) => {
  const [i, setI] = useState(0);
  if (!open || !cards.length) return null;
  const idx = Math.min(i, cards.length - 1);
  const card = cards[idx];
  const last = idx >= cards.length - 1;
  const next = () => last ? onClose() : setI(v => Math.min(v + 1, cards.length - 1));
  const prev = () => setI(v => Math.max(v - 1, 0));

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9500, background: 'var(--paper)',
      display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto',
      animation: 'e2FadeUp 0.25s ease both',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'calc(16px + env(safe-area-inset-top,0px)) 18px 8px' }}>
        <div style={{ flex: 1, display: 'flex', gap: 5 }}>
          {cards.map((_, k) => (
            <div key={k} style={{ flex: 1, height: 3.5, borderRadius: 999, background: k <= idx ? 'var(--ember)' : 'var(--paper-3)', transition: 'background 0.3s' }} />
          ))}
        </div>
        {!last && <button onClick={onClose} style={{ fontSize: 13.5, color: 'var(--ink-3)', fontWeight: 600, padding: '4px 6px', background: 'none', border: 'none' }}>{skipLabel}</button>}
      </div>

      <div key={card.key} style={{ flex: 1, overflowY: 'auto', padding: '14px 24px 24px', display: 'flex', flexDirection: 'column', justifyContent: 'center', animation: 'e2FadeUp 0.3s ease both' }}>
        {card.illo && <div style={{ marginBottom: 26 }}>{card.illo}</div>}
        <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ember)', fontWeight: 700, marginBottom: 8 }}>{card.eyebrow}</div>
        <h2 style={{ fontFamily: 'Instrument Serif, Georgia, serif', fontSize: 30, lineHeight: 1.1, color: 'var(--ink)', margin: '0 0 14px' }}>{card.title}</h2>
        {card.isList && card.list ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            {card.list.map(([icon, t, d], k) => (
              <div key={k} style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                <div style={{ width: 38, height: 38, borderRadius: 11, background: 'var(--ember-soft, #3C2A1C)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name={icon} size={18} color="var(--ember)" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)' }}>{t}</div>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.45 }}>{d}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 15.5, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0 }}>{card.body}</p>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px calc(20px + env(safe-area-inset-bottom,0px))', borderTop: '1px solid var(--line)' }}>
        {idx > 0
          ? <button onClick={prev} style={{ padding: '13px 18px', borderRadius: 12, background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink-2)', fontSize: 14, fontWeight: 600 }}>Voltar</button>
          : <div style={{ width: 1 }} />}
        <button onClick={next} style={{
          flex: 1, padding: '14px', borderRadius: 12, background: 'var(--ember)', color: '#fff',
          fontSize: 15, fontWeight: 700, border: 'none', boxShadow: '0 10px 24px -8px rgba(199,83,26,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          {last ? startLabel : 'Próximo'}
          {!last && <Icon name="chevron-right" size={16} color="#fff" />}
        </button>
      </div>
    </div>
  );
};

// ── Tutorial da página EXPLORAR (1ª visita, revisível em Ajustes) ─────────────
export const EXPLORE_TOUR_CARDS: TourCard[] = [
  { key: 'discover', illo: <MiniSearchPreview />, eyebrow: 'Explorar', title: 'Descubra leitores e clubes', body: 'Aqui é onde você encontra gente e comunidades que leem o que você curte — seu ponto de partida pra ler acompanhado.' },
  { key: 'tools', isList: true, eyebrow: 'O que dá pra fazer', title: 'Busque e conecte', list: [
    ['search', 'Buscar pessoas e clubes', 'Procure leitores pelo @ ou nome, e clubes por tema (fantasia, romance, estudo…).'],
    ['plus', 'Seguir leitores', 'Siga quem te inspira e veja o que eles leem na sua Home.'],
    ['users', 'Entrar em clubes públicos', 'Leia junto com a comunidade e aprenda com quem já leu o livro.'],
  ] },
  { key: 'yours', eyebrow: 'Seus clubes aqui', title: 'Seu clube também aparece', body: 'Quando você cria um clube público, ele aparece aqui pra a comunidade descobrir e entrar — é assim que sua leitura ganha gente junto.', final: true },
];
export const ExploreTour: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <SlideTour open={open} cards={EXPLORE_TOUR_CARDS} onClose={onClose} startLabel="Explorar" />
);
